import type { FC, SVGProps } from "react";
import KkScrewGraphic, { type KkScrewSize, type MillimetersToPixels } from "./KkScrew";

type SnapProps = SVGProps<SVGSVGElement> & {
  screw?: Partial<KkScrewSize> | null;
  showHead?: boolean;
};

export const SNAP_CONNECTOR_BASE_WIDTH_MM = 28;
export const SNAP_CONNECTOR_BASE_THICKNESS_MM = 4;
export const SNAP_CONNECTOR_HEAD_SIZE_MM = 7;
export const SNAP_CONNECTOR_GROOVE_CLEARANCE_MM = 2;

const FILL_COLOR = "#5b5b5b";
const STROKE_COLOR = "#111";
const TOP_BORDER_COLOR = "#707070";
const STROKE_WIDTH = 0.16;
const millimetersToGraphicUnits: MillimetersToPixels = (millimeters) => millimeters;

const Snap: FC<SnapProps> = ({ screw, showHead = true, ...props }) => {
  const headX = (SNAP_CONNECTOR_BASE_WIDTH_MM - SNAP_CONNECTOR_HEAD_SIZE_MM) / 2;
  const headRightX = headX + SNAP_CONNECTOR_HEAD_SIZE_MM;
  const baseBottomY = SNAP_CONNECTOR_BASE_THICKNESS_MM;
  const graphicHeight = SNAP_CONNECTOR_BASE_THICKNESS_MM + (showHead ? SNAP_CONNECTOR_HEAD_SIZE_MM : 0);
  const chamfer = 0.35;
  const screwCenterX = SNAP_CONNECTOR_BASE_WIDTH_MM / 2;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${SNAP_CONNECTOR_BASE_WIDTH_MM} ${graphicHeight}`}
      aria-hidden="true"
      overflow="visible"
      shapeRendering="geometricPrecision"
      {...props}
    >
      {screw && (
        <KkScrewGraphic
          screw={screw}
          toPixels={millimetersToGraphicUnits}
          centerX={screwCenterX}
          headBottomY={0}
        />
      )}
      <path
        d={`M 0 ${chamfer}
          L ${chamfer} 0
          H ${SNAP_CONNECTOR_BASE_WIDTH_MM - chamfer}
          L ${SNAP_CONNECTOR_BASE_WIDTH_MM} ${chamfer}
          V ${baseBottomY - chamfer}
          L ${SNAP_CONNECTOR_BASE_WIDTH_MM - chamfer} ${baseBottomY}
          H ${chamfer}
          L 0 ${baseBottomY - chamfer}
          Z`}
        fill={FILL_COLOR}
        stroke={STROKE_COLOR}
        strokeLinejoin="round"
        strokeWidth={STROKE_WIDTH}
      />
      {showHead && (
        <rect
          x={headX}
          y={baseBottomY}
          width={SNAP_CONNECTOR_HEAD_SIZE_MM}
          height={SNAP_CONNECTOR_HEAD_SIZE_MM}
          fill={FILL_COLOR}
          stroke={STROKE_COLOR}
          strokeWidth={STROKE_WIDTH}
        />
      )}
      <path
        d={`M ${chamfer} 0
          H ${SNAP_CONNECTOR_BASE_WIDTH_MM - chamfer}
          L ${SNAP_CONNECTOR_BASE_WIDTH_MM} ${chamfer}
          H 0
          Z`}
        fill={TOP_BORDER_COLOR}
        stroke={STROKE_COLOR}
        strokeLinejoin="round"
        strokeWidth={STROKE_WIDTH}
      />
      {showHead && (
        <path
          d={`M ${headX - 0.85} ${baseBottomY}
            Q ${headX} ${baseBottomY - 0.7} ${headX + 0.85} ${baseBottomY - 0.7}
            H ${headRightX - 0.85}
            Q ${headRightX} ${baseBottomY - 0.7} ${headRightX + 0.85} ${baseBottomY}`}
          fill="none"
          stroke={STROKE_COLOR}
          strokeWidth={STROKE_WIDTH * 0.75}
        />
      )}
    </svg>
  );
};

export default Snap;
